import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import SearchBar from "../components/SearchBar";
import Spinner from "../components/Spinner";
import AppIcon from "../components/AppIcon";
import LicenseUsageBar from "../components/LicenseUsageBar";
import vppCatalogService from "../services/vppCatalogService";
import { formatDateTime } from "../utils/tokenExpiry";
import isAdministrator from "../utilities/checkPrivileges";

function Applications() {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const admin = isAdministrator();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [configured, setConfigured] = useState(true);
  const [assets, setAssets] = useState([]);
  const [vpp, setVpp] = useState(null);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [query, setQuery] = useState("");
  const [productType, setProductType] = useState("");

  useEffect(() => {
    if (!user) navigate("/login");
  }, [user, navigate]);

  const loadCatalog = async ({ refresh = false } = {}) => {
    try {
      if (refresh) setRefreshing(true);
      else setLoading(true);
      const data = await vppCatalogService.getAssets({ refresh });
      setConfigured(Boolean(data.configured));
      setAssets(data.assets || []);
      setVpp(data.vpp || null);
      setFetchedAt(data.fetchedAt || null);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "App-Katalog konnte nicht geladen werden.");
      setAssets([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (user) loadCatalog();
  }, [user]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return assets.filter((app) => {
      if (productType && String(app.productType || "") !== productType) return false;
      if (!q) return true;
      const haystack = [
        app.name,
        app.bundleId,
        app.adamId,
        app.artistName,
        app.sellerName,
        app.genre,
        app.isB2BCustomApp ? "b2b custom" : "",
        ...(app.supportedPlatforms || []),
        ...(app.assignedGroups || []).map((g) => g.groupName),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [assets, query, productType]);

  if (!user) return null;
  if (loading) return <Spinner />;

  if (!configured) {
    return (
      <div>
        <h2 className="mb-2">Applications</h2>
        <p className="text-muted mb-3">
          Katalog der in ABM/ASM gekauften Apps und Bücher.
        </p>
        <div className="alert alert-secondary">
          Noch kein Inhalte-Token hinterlegt.
          {admin ? (
            <>
              {" "}Bitte unter{" "}
              <Link to="/settings">Einstellungen</Link> das Token aus Apple Business Manager
              oder Apple School Manager hochladen.
            </>
          ) : (
            " Ein Administrator muss das Inhalte-Token unter Einstellungen hinterlegen."
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
        <div>
          <h2 className="mb-1">Applications</h2>
          <p className="text-muted mb-0">
            Gekaufte Apps aus ABM/ASM. Eine App öffnen für Details, Gruppen, Configs und Lizenzen.
            Name, Bundle-ID und Icons werden über Apples Enterprise-Katalog angereichert (gecacht, 7 Tage).
          </p>
        </div>
        <button
          type="button"
          className="btn btn-outline-secondary"
          disabled={refreshing}
          onClick={() => loadCatalog({ refresh: true })}
        >
          {refreshing ? "Aktualisiere…" : "Von Apple aktualisieren"}
        </button>
      </div>

      <div className="d-flex flex-wrap gap-3 small text-muted mb-3">
        {vpp?.locationName && <span><strong>Standort:</strong> {vpp.locationName}</span>}
        {vpp?.orgName && <span><strong>Organisation:</strong> {vpp.orgName}</span>}
        <span><strong>Einträge:</strong> {visible.length} / {assets.length}</span>
        {fetchedAt && <span><strong>Stand:</strong> {formatDateTime(fetchedAt)}</span>}
      </div>

      <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
        <div className="flex-grow-1" style={{ minWidth: 220 }}>
          <SearchBar
            searchHandler={setQuery}
            value={query}
            placeholder="Name, Hersteller, Bundle-ID oder Adam-ID"
          />
        </div>
        <select
          className="form-select"
          style={{ maxWidth: 180 }}
          value={productType}
          onChange={(e) => setProductType(e.target.value)}
        >
          <option value="">Alle Typen</option>
          <option value="App">Apps</option>
          <option value="Book">Bücher</option>
        </select>
      </div>

      <div className="table-responsive">
        <table className="table table-hover align-middle">
          <thead>
            <tr>
              <th style={{ width: 64 }}></th>
              <th>App</th>
              <th>App Identifier</th>
              <th>Hersteller</th>
              <th>Gruppen</th>
              <th style={{ minWidth: 140 }}>Lizenzen</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-muted">Keine Treffer.</td>
              </tr>
            ) : (
              visible.map((app) => (
                <tr
                  key={`${app.adamId}-${app.pricingParam || ""}`}
                  role="button"
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate(`/applications/${app.adamId}`)}
                >
                  <td>
                    <AppIcon src={app.artworkUrlSmall || app.artworkUrl} name={app.name} size={52} />
                  </td>
                  <td>
                    <Link
                      to={`/applications/${app.adamId}`}
                      className="text-decoration-none"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {app.name || app.bundleId || app.adamId}
                    </Link>
                    {app.isB2BCustomApp ? (
                      <span className="badge text-bg-info ms-2">Custom B2B</span>
                    ) : null}
                    {!app.name && app.adamId ? (
                      <span className="badge text-bg-warning ms-2" title="Metadaten fehlen – bitte „Von Apple aktualisieren“">
                        nur Adam-ID
                      </span>
                    ) : null}
                    <div className="small text-muted">
                      {app.genre ? `${app.genre} · ` : ""}
                      {app.supportedPlatforms?.length ? app.supportedPlatforms.join(", ") : (app.productType || "App")}
                    </div>
                  </td>
                  <td>
                    {app.bundleId ? (
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-decoration-none"
                        title="App Identifier kopieren"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(app.bundleId).then(
                            () => toast.success("App Identifier kopiert."),
                            () => toast.error("Kopieren nicht möglich.")
                          );
                        }}
                      >
                        <code>{app.bundleId}</code>
                      </button>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td>{app.artistName || app.sellerName || "—"}</td>
                  <td>
                    {(app.assignedGroups || []).length
                      ? app.assignedGroups.map((g) => g.groupName).join(", ")
                      : <span className="text-muted">—</span>}
                  </td>
                  <td>
                    <LicenseUsageBar
                      compact
                      assigned={app.assignedCount}
                      available={app.availableCount}
                      retired={app.retiredCount}
                      total={app.totalCount}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Applications;
